import React, { FC, useEffect, useState } from 'react';

import { LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import styles from './ContextModelSensorMapping.module.scss';
import { dataGridFocusStyles } from 'styles';
import { SensorMappingCustomToolbar } from './SensorMappingTable/SensorMappingCustomToolbar';
import { sensorMappingColumns } from './SensorMappingTable/SensorMappingTableDefinition';
import { PowerplantSensorMappingModel } from 'pages/ContextManagerPage/PowerplantSensorMappingModel';
import { SensorMappingModel } from './SensorMappingModel';
import OverrideSensorNameDialog from './OverrideSensorNameDialog/OverrideSensorNameDialog';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import { WordMappingModel } from 'pages/ContextManagerPage/ContextManagementView/WordMappingModel';
import { SensorModel } from 'pages/TimeseriesPage/SensorModel';
import { TimeseriesService } from 'pages/TimeseriesPage/TimeseriesService';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface ContextModelSensorMappingProps {
  powerplantName: string;
  wordMappings: WordMappingModel[];
}

const ContextModelSensorMapping: FC<ContextModelSensorMappingProps> = (
  {
    powerplantName,
    wordMappings
  }
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState<boolean>(false)
  const [powerplantSensorMappings, setPowerplantSensorMappings] = useState<PowerplantSensorMappingModel | null>(null)
  const [selectedSensorMapping, setSelectedSensorMapping] = useState<SensorMappingModel | null>(null);
  const [sensorMappings, setSensorMappings] = useState<SensorMappingModel[]>([])
  const [sensors, setSensors] = useState<SensorModel[]>([]);

  useEffect(() => {
    getAll(powerplantName);
  }, [powerplantName]);

  useEffect(() => {
    if (!powerplantSensorMappings || !powerplantSensorMappings.mappings) {
      return;
    }

    const sensorMappingsArrays: SensorMappingModel[] = Object.entries(powerplantSensorMappings.mappings).map(([key, value]) => {
      const matchedSensor  = sensors.find(sensor => sensor.name === key);

      if (!matchedSensor) return;

      return {
        sensorId: matchedSensor._id,
        override: matchedSensor.sensorname_override,
        sensor: key,
        tag: value,
      }
    }).filter((item): item is SensorMappingModel => item !== undefined);

    setSensorMappings(sensorMappingsArrays);
  }, [powerplantSensorMappings, sensors]);

  const closeSensorNameOverrideDialog = (): void => {
    setSelectedSensorMapping(null);
    setIsOverrideDialogOpen(false);
  }

  /* Fetch sensors and sensor mappings */
  const getAll = async (plantName: string): Promise<void> => {
    setIsFetching(true);

    try {
      await Promise.all([
        getSensors(),
        getPowerplantSensorMappings(plantName)
      ])
    } catch (error) {
      console.error('Failed getting data', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed getting sensors and/or powerplant sensor mappings. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsFetching(false);
    }
  }

  const getPowerplantSensorMappings = async (plantName: string): Promise<void> => {
    try {
    const fetchedPowerplantSensorMappings: PowerplantSensorMappingModel = await ContextManagerService.getPowerplantSensorMappings(plantName);

    if (!fetchedPowerplantSensorMappings || !fetchedPowerplantSensorMappings.mappings) {
      throw new Error('No sensor mappings returned by the service');
    }

    setPowerplantSensorMappings(fetchedPowerplantSensorMappings);
    } catch (error) {
      setPowerplantSensorMappings(null);
      throw error; // Rethrow to handle it more centrally
    }
  }

  const getSensors = async (): Promise<void> => {
    try {
      const fetchedSensors = await TimeseriesService.getSensors();

      if (!fetchedSensors) {
        throw new Error('No sensors returned by the service');
      }

      setSensors(fetchedSensors);
    } catch (error) {
      setSensors([]);
      throw error;  // Rethrow to handle it more centrally
    }
  }

  const openSensorNameOverrideDialog = (row: SensorMappingModel): void => {
    setSelectedSensorMapping(row);
    setIsOverrideDialogOpen(true);
  }

  const sensorUpdateHandler = async (): Promise<void> => {
    await getAll(powerplantName);
  };

  return (
    <div
      className={styles.ContextModelSensorMapping}
      data-testid="ContextModelSensorMapping"
    >
      {isOverrideDialogOpen && selectedSensorMapping &&
        <OverrideSensorNameDialog
          onClose={closeSensorNameOverrideDialog}
          onSensorUpdate={sensorUpdateHandler}
          sensorMapping={selectedSensorMapping}
          wordMappings={wordMappings}
        />
      }

      <DataGrid
        rows={sensorMappings}
        columns={sensorMappingColumns(openSensorNameOverrideDialog)}
        slots={{
          loadingOverlay: LinearProgress,
          toolbar: () => SensorMappingCustomToolbar(),
        }}
        loading={isFetching}
        className="project-data-grid"
        getRowId={row => row.sensorId}
        sx={dataGridFocusStyles}
        // sx={{ ...dataGridFocusStyles, ...dataGridFontSizeStyles }}
        disableRowSelectionOnClick
        disableColumnMenu
        rowHeight={44}
        // pageSizeOptions={[]}
        // showCellVerticalBorder
        // showColumnVerticalBorder
        // onCellClick={(params, event, details) => cellClickHandler(params)}
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  )
};

export default ContextModelSensorMapping;
